import { useState, useEffect } from "react";
import { FlowModelService } from "../services/FlowService";

export class FlowViewModel {
  constructor() {
    this.service = new FlowModelService();
    this.listeners = new Set();
  }

  // Subscribe to changes
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notifyListeners() {
    console.log("ViewModel notifying listeners, count:", this.listeners.size);
    this.listeners.forEach((listener) => listener());
  }

  // Getters
  getNodes() {
    return this.service.getAllNodes();
  }

  getEdges() {
    return this.service.getAllEdges();
  }

  getState() {
    return this.service.getState();
  }

  // Node operations
  addNode(type, position, additionalData = {}) {
    let node;

    switch (type) {
      case "custom":
        node = this.service.createCustomNode(position);
        break;
      case "buttonNode":
        node = this.service.createButtonNode(position);
        break;
      case "normalMessage":
        node = this.service.createNormalMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "interactiveMessage":
        node = this.service.createInteractiveMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
        };
        if (additionalData.onSectionsChange) {
          node.data.onSectionsChange = additionalData.onSectionsChange;
        }
        if (additionalData.onDeleteRowNode) {
          node.data.onDeleteRowNode = additionalData.onDeleteRowNode;
        }
        break;
      case "mediaMessage":
        node = this.service.createMediaMessageNode(position);
        node.data.onChangeMediaType = (id, value) => {
          this.handleNodeChange({ id, field: "mediaType", value });
        };
        node.data.onChangeCaption = (id, value) => {
          this.handleNodeChange({ id, field: "caption", value });
        };
        node.data.onChangeFileName = (id, value) => {
          this.handleNodeChange({ id, field: "fileName", value });
        };
        node.data.onChangeFilePath = (id, value) => {
          this.handleNodeChange({ id, field: "filePath", value });
        };
        node.data.onChangeFile = (id, value) => {
          this.handleNodeChange({ id, field: "selectedFile", value });
        };
        node.data.onChangePreviewUrl = (id, value) => {
          this.handleNodeChange({ id, field: "previewUrl", value });
        };
        node.data.onDelete = (id) => {
          this.deleteNode(id);
        };
        break;
      case "catalogMessage":
        node = this.service.createCatalogMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "templateMessage":
        node = this.service.createTemplateMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "questionMessage":
        node = this.service.createQuestionMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "flowMessage":
        node = this.service.createFlowMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "conditionNode":
        node = this.service.createConditionNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "verticalActionNode":
        node = this.service.createVerticalActionNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "webhookMessage":
        node = this.service.createWebhookMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "paymentMessage":
        node = this.service.createPaymentMessageNode(position);
        node.data.onDelete = (id) => {
          this.deleteNode(id);
          this.notifyListeners();
        };
        break;
      case "labelChildNode":
        node = this.service.createLabelChildNode(position);
        break;
      case "rowNode":
        node = this.service.createRowNode(position, additionalData);
        break;
      default:
        throw new Error(`Unknown node type: ${type}`);
    }

    this.notifyListeners();
    return node;
  }

  updateNode(id, data) {
    this.service.updateNode(id, data);
    this.notifyListeners();
  }

  // Method to mark node as updated
  markNodeAsUpdated(id) {
    this.service.updateNode(id, { isUpdated: true });
    this.notifyListeners();
  }

  // Method to reset node update flag
  resetNodeUpdateFlag(id) {
    this.service.updateNode(id, { isUpdated: false });
    this.notifyListeners();
  }

  deleteNode(id) {
    console.log("ViewModel deleteNode called with id:", id);
    console.log("Nodes before deletion:", this.service.getAllNodes().length);
    this.service.deleteNode(id);
    console.log("Nodes after deletion:", this.service.getAllNodes().length);
    this.notifyListeners();
  }

  // Edge operations
  addEdge(sourceId, targetId, sourceHandle, targetHandle) {
    const edge = this.service.createEdge(
      sourceId,
      targetId,
      sourceHandle,
      targetHandle
    );
    this.notifyListeners();
    return edge;
  }

  deleteEdge(id) {
    this.service.deleteEdge(id);
    this.notifyListeners();
  }

  // Event handlers
  handleNodeChange(event) {
    this.service.handleNodeChange(event);
    this.notifyListeners();
  }

  handleDragStart(event) {
    // Handle drag start logic
    console.log("Drag started:", event);
  }

  handleDrop(event) {
    // Handle drop logic
    console.log("Drop event:", event);
  }

  // Node type specific handlers
  handleCustomNodeChange(id, field, value) {
    this.handleNodeChange({ id, field, value });
  }

  handleButtonNodeChange(id, value) {
    this.handleNodeChange({ id, field: "buttonLabel", value });
  }

  handleNormalMessageChange(id, value) {
    this.handleNodeChange({ id, field: "text", value });
  }

  handleInteractiveMessageChange(id, field, value) {
    this.handleNodeChange({ id, field, value });
  }

  handleMediaMessageChange(id, field, value) {
    this.handleNodeChange({ id, field, value });
  }

  handleLabelChildChange(id, index, value) {
    const node = this.service.getNode(id);
    if (node && Array.isArray(node.data.labels)) {
      const newLabels = [...node.data.labels];
      newLabels[index] = value;
      this.handleNodeChange({ id, field: "labels", value: newLabels });
    }
  }

  // Export/Import
  exportFlow() {
    return this.service.exportFlow();
  }

  importFlow(flowData) {
    this.service.importFlow(flowData);
    this.notifyListeners();
  }

  // async getFlow(chatbotdId) {
  //   const flowData = await FlowService.getFlow(chatbotdId);
  //   this.service.importFlow(flowData);
  //   this.notifyListeners();
  // }

  clearFlow() {
    this.service.clearFlow();
    this.notifyListeners();
  }

  // Validation
  validateNodeData(nodeData, nodeType) {
    return this.service.validateNodeData(nodeData, nodeType);
  }

  // Utility methods
  getNodeById(id) {
    return this.service.getNode(id);
  }

  canConnect(sourceId, targetId) {
    return this.service.model.canConnect(sourceId, targetId);
  }
}

// React Hook for using the ViewModel
export const useFlowViewModel = () => {
  const [viewModel] = useState(() => new FlowViewModel());
  const [, forceUpdate] = useState({});

  useEffect(() => {
    const unsubscribe = viewModel.subscribe(() => {
      forceUpdate({});
    });

    return unsubscribe;
  }, [viewModel]);

  return viewModel;
};
